import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NextEvent from '../home/NextEvent';
import '../styles/MainContainer.css';
import '../home/Home.css';
import api from '../wrapper/API';
import { getFutureEvents, prefetchLeaderEvents, prefetchReferenceData } from '../../offline/offlineApi';
import SmallHomeOption from '../home/SmallHomeOption';
import SmallHomeAddOption from '../home/SmallHomeAddOption';
import { isAdmin } from '../../util/auth';
import { ROUTES } from '../../util/routes';
import EventContextMenu from '../event/EventContextMenu';
import { useTour } from '../context/TourContext';
import { useOffline } from '../context/OfflineContext';
import { HOME_TOUR_KEY, homeTourSteps } from '../tour/tours/homeTour';
import useMailQueueBadge from '../../hooks/useMailQueueBadge';
import OfflineBanner from '../shell/OfflineBanner';
import useWikis from '../../hooks/useWikis';
import WikiEditorPopup, { WIKI_EDITOR_POPUP } from '../wiki/WikiEditorPopup';
import { usePopup } from '../context/PopupContext';

function Home() {
  const [posts, setPosts] = useState([]);
  const { isTourCompleted, startTour, tourReady } = useTour();
  const { isOffline, onlineEpoch } = useOffline();
  const { wikis, loaded: wikisLoaded, failed: wikisFailed } = useWikis();
  const { openPopup } = usePopup();
  const navigate = useNavigate();
  // The optional tour step "Wikis" is counted against the DOM: start only once the section is final.
  const wikisSettled = wikisLoaded || wikisFailed;

  useEffect(() => {
    if (!tourReady) return;
    if (posts.length === 0) return;
    if (!wikisSettled) return;
    if (!isTourCompleted(HOME_TOUR_KEY)) {
      startTour(HOME_TOUR_KEY, homeTourSteps);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourReady, posts.length, wikisSettled]);

  useEffect(() => {
    // Beim Öffnen der App alles offline Nötige im Hintergrund vorladen: Wikis + Preisliste für alle,
    // Spieltage nur für deren Leiter (nächster + weitere der nächsten Tage). Beides drosselt selbst.
    // Die Wikis teilen sich den Request mit den Kacheln unten (useWikis).
    prefetchReferenceData().catch(() => {});

    // Offline-fähig: online holen + cachen, offline aus dem Cache (siehe getFutureEvents).
    // onlineEpoch: nach "Erneut verbinden" frisch vom Server laden.
    getFutureEvents()
      .then(data => {
        if (data.length !== 0) {
          setPosts(data);
          prefetchLeaderEvents(data);
        } else {
          // Online-Fallback (nur sinnvoll mit Verbindung): alle Events.
          api.get('/Event/GetAllCombined')
            .then(r => { setPosts(r.data); prefetchLeaderEvents(r.data); })
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [onlineEpoch]);

  const admin = isAdmin();
  const dlqCount = useMailQueueBadge({ enabled: admin });
  // Without wikis the section only makes sense for admins (add tile).
  const showWikis = admin || wikis.length > 0;
  // The editor needs the loaded list (default position = at the end, duplicate-name check).
  const addWikiBlockedReason = isOffline
    ? 'Offline nicht verfügbar'
    : !wikisLoaded
      ? (wikisFailed ? 'Wikis konnten nicht geladen werden' : 'Wikis werden geladen …')
      : null;

  return (
    <React.Fragment>
      <main className="defaultMain">
        <OfflineBanner />
        <div className="homeContainer">
          {/* Next event */}
          <div className="homeSection" data-tour="home-next-event">
            <span className="homeSectionLabel">Nächster Spieltag</span>
            <NextEvent post={posts[0] ?? null} />
          </div>

          {/* Game operations: what runs the next events */}
          <div className="homeSection">
            <span className="homeSectionLabel">Spielbetrieb</span>
            <div className="homeQuickGrid">
              {admin && <SmallHomeOption navigate={ROUTES.MAIL_QUEUE} icon="outbox" text="Mailing-Fehler" badgeCount={dlqCount} />}
              {admin && <SmallHomeOption navigate={ROUTES.EVENT_ANNOUNCEMENT} icon="campaign" text="Event Prozesse" />}
              <SmallHomeOption data-tour="home-prices" navigate={ROUTES.PRICES} icon="sell" text="Preisliste" />
            </div>
          </div>

          {/* Wikis (dynamic, by position) + add tile for admins */}
          {showWikis && (
            <div className="homeSection" data-tour="home-wikis">
              <span className="homeSectionLabel">Wikis</span>
              <div className="homeQuickGrid">
                {wikis.map(w => (
                  <SmallHomeOption key={w.id} navigate={ROUTES.wiki(w.id)} icon={w.icon} text={w.name} />
                ))}
                {admin && (
                  <SmallHomeAddOption
                    text="Neues Wiki"
                    onClick={() => openPopup(WIKI_EDITOR_POPUP, 'new')}
                    disabled={addWikiBlockedReason != null}
                    title={addWikiBlockedReason ?? 'Neues Wiki anlegen'}
                  />
                )}
              </div>
            </div>
          )}

          {/* Administration (admins), alphabetical */}
          {admin && (
            <div className="homeSection">
              <span className="homeSectionLabel">Verwaltung</span>
              <div className="homeQuickGrid">
                <SmallHomeOption navigate={ROUTES.PARTICIPANT_GROUPS} icon="groups" text="Gruppen" />
                <SmallHomeOption navigate={ROUTES.LOCATIONS} icon="location_on" text="Locations" />
                <SmallHomeOption navigate={ROUTES.FLAT_RATES} icon="request_quote" text="Pauschalen & Abzüge" />
                <SmallHomeOption navigate={ROUTES.PROCESS_TEMPLATES} icon="account_tree" text="Prozessvorlagen" />
                <SmallHomeOption navigate={ROUTES.TEXTBLOCK_EDITOR} icon="edit" text="Textbausteine" />
              </div>
            </div>
          )}
        </div>
      </main>
      {posts.length > 0 && <EventContextMenu posts={posts} setPosts={setPosts} type="nextevent" />}
      {admin && <WikiEditorPopup onSaved={page => navigate(ROUTES.wiki(page.id))} />}
    </React.Fragment>
  );
}

export default Home;
